import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { trainingAPI, usersAPI, aiAPI, analyticalMethodsAPI, commercialConfigAPI, rolesAPI } from '../services/api';
import { Modal, ModalFooter } from '../components/Modals';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import useApprovalRights from '../hooks/useApprovalRights';
import AIResultPanel from '../components/AIResultPanel';
import useAIAction from '../hooks/useAIAction';
import { FaRobot, FaPaperclip, FaTimes, FaCheck, FaHourglassHalf, FaExclamationTriangle, FaGraduationCap, FaSyncAlt, FaDownload, FaFileUpload } from 'react-icons/fa';
import FilterStatCards, { matchesGroup } from '../components/FilterStatCards';
import TablePagination from '../components/TablePagination';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const TRAINING_TYPES = ['SOP', 'Protocol', 'AMV', 'Visual', 'CCV'];

const MANDATORY_STAGES = [
  { key: 'cleaning', label: 'Cleaning' },
  { key: 'sampling', label: 'Sampling' },
  { key: 'visual', label: 'Visual Inspection' },
  { key: 'testing', label: 'Testing' }
];

const getRows = (payload) => {
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const createEmptyTypeForm = () => ({
  training_code: '',
  training_name: '',
  training_type: 'SOP',
  document_reference: '',
  validity_period: '',
  mandatory_for_stages: [],
  mandatory_for_roles: []
});

const createEmptyRecordForm = () => ({
  training_id: '',
  user_id: canLogForOthers ? '' : (currentUserId != null ? String(currentUserId) : ''),
  training_date: '',
  trainer_name: '',
  is_completed: true,
  manual_reference: '',
  manual_files: []
});

const BADGE_BASE = { padding: '3px 12px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-md)', whiteSpace: 'nowrap' };

const Badge = ({ bg, color, border, children, title }) => (
  <span title={title} style={{ ...BADGE_BASE, background: bg, color, border: `1px solid ${border}` }}>
    {children}
  </span>
);

const daysToExpiry = (record) => (record.expiry_date ? (new Date(record.expiry_date) - new Date()) / (1000 * 60 * 60 * 24) : Infinity);
const RECORD_GROUPS = [
  { key: 'valid', title: 'Valid', icon: FaCheck, color: 'success', match: (r) => daysToExpiry(r) > 30 },
  { key: 'expiring', title: 'Expiring Soon', icon: FaHourglassHalf, color: 'warning', match: (r) => { const d = daysToExpiry(r); return d >= 0 && d <= 30; } },
  { key: 'expired', title: 'Expired', icon: FaExclamationTriangle, color: 'danger', match: (r) => daysToExpiry(r) < 0 },
  { key: 'sync', title: 'LMS Sync Issues', icon: FaSyncAlt, color: 'info', match: (r) => r.lms_sync_status === 'failed' },
];

const expiryBadge = (expiryDate) => {
  if (!expiryDate) {
    return <Badge bg="var(--success-bg)" color="var(--success-text)" border="var(--success-border)">No Expiry</Badge>;
  }
  const daysAway = (new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24);
  if (daysAway < 0) {
    return <Badge bg="var(--danger-bg)" color="var(--danger-text)" border="var(--danger-border)">Expired</Badge>;
  }
  if (daysAway <= 30) {
    return <Badge bg="var(--warning-bg)" color="var(--warning-text)" border="var(--warning-border)">Expiring Soon</Badge>;
  }
  return <Badge bg="var(--success-bg)" color="var(--success-text)" border="var(--success-border)">Valid</Badge>;
};

const lmsSyncBadge = (record) => {
  if (record.lms_sync_status === 'synced') {
    return <Badge bg="var(--success-bg)" color="var(--success-text)" border="var(--success-border)">Synced to AiventraLMS</Badge>;
  }
  if (record.lms_sync_status === 'failed') {
    return (
      <Badge bg="var(--danger-bg)" color="var(--danger-text)" border="var(--danger-border)" title={record.lms_sync_error || 'Sync failed'}>
        Sync Failed
      </Badge>
    );
  }
  if (record.lms_sync_status === 'manual') {
    return <Badge bg="var(--warning-bg)" color="var(--warning-text)" border="var(--warning-border)" title={record.manual_reference || 'Manual record'}>Manual</Badge>;
  }
  return <Badge bg="var(--surface-light)" color="var(--text-secondary)" border="var(--border-color)">Not Synced</Badge>;
};

const mandatoryBadge = (type) => {
  const stages = Array.isArray(type.mandatory_for_stages) ? type.mandatory_for_stages : [];
  const roles = Array.isArray(type.mandatory_for_roles) ? type.mandatory_for_roles : [];
  if (!type.is_mandatory || (stages.length === 0 && roles.length === 0)) {
    return <span className="text-muted">-</span>;
  }
  const stageLabel = stages.map((stage) => MANDATORY_STAGES.find((s) => s.key === stage)?.label || stage).join(', ');
  const roleLabel = roles.length ? `Roles: ${roles.join(', ')}` : '';
  return [stageLabel, roleLabel].filter(Boolean).join(' · ') || 'All roles';
};

let currentUserRole = '';
let currentUserId = null;
try {
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  currentUserRole = storedUser?.role || '';
  currentUserId = storedUser?.user_id ?? null;
} catch (e) { currentUserRole = ''; }
const canManageTypes = ['Admin', 'Super Admin'].includes(currentUserRole);
// Any authenticated user can log their own training completion now (Head QA then
// approves it before it counts for mandatory-training compliance); QA/Admin/Super Admin
// can additionally log on behalf of someone else.
const canLogForOthers = ['QA', 'Admin', 'Super Admin'].includes(currentUserRole);
const canLogRecords = true;
const Training = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Who may approve training records: Settings → Approval Flow (Training Record).
  const { canFinal: canApproveRecords } = useApprovalRights('training_record.approval');
  const [loading, setLoading] = useState(true);
  const [recordUserFilter, setRecordUserFilter] = useState('');
  const [typeImportPreview, setTypeImportPreview] = useState(null); // { fileName, rows }
  const [typeImportResult, setTypeImportResult] = useState(null);
  const [typeImporting, setTypeImporting] = useState(false);
  const typeImportInputRef = useRef(null);
  const [savingType, setSavingType] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [records, setRecords] = useState([]);
  const [recordCard, setRecordCard] = useState('All');
  const [users, setUsers] = useState([]);
  const [typeForm, setTypeForm] = useState(createEmptyTypeForm());
  const [recordForm, setRecordForm] = useState(createEmptyRecordForm());
  const [lmsSyncEnabled, setLmsSyncEnabled] = useState(false);
  const [lmsEmployeeSync, setLmsEmployeeSync] = useState(false);
  const [syncingLmsEmployees, setSyncingLmsEmployees] = useState(false);
  const [uploadingManual, setUploadingManual] = useState(false);
  const [roles, setRoles] = useState([]);
  const ai = useAIAction();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [typesRes, recordsRes, usersRes, runtimeRes, rolesRes] = await Promise.all([
          trainingAPI.getAll(),
          trainingAPI.getRecords(),
          usersAPI.getAll(),
          commercialConfigAPI.getRuntime(),
          rolesAPI.getAll()
        ]);
        setTrainingTypes(getRows(typesRes));
        setRecords(getRows(recordsRes));
        setUsers(getRows(usersRes));
        setLmsSyncEnabled(!!runtimeRes?.data?.data?.training?.lms_sync_enabled);
        setLmsEmployeeSync(!!runtimeRes?.data?.data?.integrations?.aiventra_lms?.features?.employee_sync);
        setRoles(getRows(rolesRes).filter((r) => r.is_active));
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load training data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const refreshTypes = async () => {
    const res = await trainingAPI.getAll();
    setTrainingTypes(getRows(res));
  };

  const refreshRecords = async () => {
    const res = await trainingAPI.getRecords();
    setRecords(getRows(res));
  };

  const [esign, setEsign] = useState(null); // { kind: 'record'|'course', row, password, comment }
  const [esigning, setEsigning] = useState(false);
  const openApproveRecord = (record) => setEsign({ kind: 'record', row: record, password: '', comment: '' });
  const openApproveCourse = (type) => setEsign({ kind: 'course', row: type, password: '', comment: '' });
  const closeEsign = () => { setEsign(null); setEsigning(false); };

  const confirmEsign = async () => {
    if (!esign?.password) return toast.error('Password required');
    setEsigning(true);
    try {
      if (esign.kind === 'record') {
        await trainingAPI.approveRecord(esign.row.record_id, { password: esign.password, comment: esign.comment });
        toast.success('Training record approved');
        await refreshRecords();
      } else {
        const res = await trainingAPI.approveCourse(esign.row.training_id, { password: esign.password, comment: esign.comment });
        const pushStatus = res?.data?.lms_push_status;
        toast.success(`Training protocol approved${pushStatus === 'pushed' ? ' and pushed to LMS' : pushStatus?.startsWith('failed') ? ` (LMS push failed: ${pushStatus.replace('failed: ', '')})` : ''}`);
        await refreshTypes();
      }
      closeEsign();
    } catch (error) {
      toast.error(error?.response?.data?.message || `Failed to approve training ${esign.kind === 'record' ? 'record' : 'protocol'}`);
      setEsigning(false);
    }
  };

  const syncLmsEmployees = async () => {
    try {
      setSyncingLmsEmployees(true);
      const res = await trainingAPI.syncLmsEmployees();
      const data = res?.data?.data || {};
      const usersRes = await usersAPI.getAll();
      setUsers(getRows(usersRes));
      toast.success(`LMS employee sync: ${data.linked || 0} linked, ${data.already_linked || 0} already linked, ${data.unmatched || 0} unmatched, ${data.conflicts || 0} conflicts.`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'LMS employee sync failed');
    } finally { setSyncingLmsEmployees(false); }
  };

  const handleTypeField = (field, value) => setTypeForm((prev) => ({ ...prev, [field]: value }));
  const handleRecordField = (field, value) => setRecordForm((prev) => ({ ...prev, [field]: value }));

  const toggleMandatoryStage = (stageKey) => {
    setTypeForm((prev) => {
      const has = prev.mandatory_for_stages.includes(stageKey);
      return {
        ...prev,
        mandatory_for_stages: has
          ? prev.mandatory_for_stages.filter((s) => s !== stageKey)
          : [...prev.mandatory_for_stages, stageKey]
      };
    });
  };

  const toggleMandatoryRole = (roleName) => {
    setTypeForm((prev) => {
      const has = prev.mandatory_for_roles.includes(roleName);
      return {
        ...prev,
        mandatory_for_roles: has
          ? prev.mandatory_for_roles.filter((r) => r !== roleName)
          : [...prev.mandatory_for_roles, roleName]
      };
    });
  };

  const uploadManualFile = async (file) => {
    if (!file) return;
    try {
      setUploadingManual(true);
      const fd = new FormData();
      fd.append('file', file);
      const res = await analyticalMethodsAPI.uploadFile(fd);
      const uploaded = res?.data?.data;
      if (!uploaded) throw new Error('Upload failed');
      setRecordForm((prev) => ({
        ...prev,
        manual_files: [...prev.manual_files, { filename: uploaded.filename, originalname: uploaded.originalname }]
      }));
    } catch (error) {
      toast.error('Certificate upload failed');
    } finally {
      setUploadingManual(false);
    }
  };

  const removeManualFile = (filename) => {
    setRecordForm((prev) => ({ ...prev, manual_files: prev.manual_files.filter((f) => f.filename !== filename) }));
  };

  const saveType = async () => {
    if (!typeForm.training_code.trim() || !typeForm.training_name.trim()) {
      toast.error('Training code and name are required');
      return;
    }

    try {
      setSavingType(true);
      await trainingAPI.create({
        training_code: typeForm.training_code,
        training_name: typeForm.training_name,
        training_type: typeForm.training_type,
        document_reference: typeForm.document_reference,
        validity_period: typeForm.validity_period === '' ? null : Number(typeForm.validity_period),
        mandatory_for_stages: typeForm.mandatory_for_stages,
        mandatory_for_roles: typeForm.mandatory_for_roles
      });
      toast.success('Training type created');
      await refreshTypes();
      setTypeForm(createEmptyTypeForm());
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create training type');
    } finally {
      setSavingType(false);
    }
  };

  const saveRecord = async () => {
    if (!recordForm.training_id || !recordForm.user_id || !recordForm.training_date) {
      toast.error('Training type, user and training date are required');
      return;
    }

    try {
      setSavingRecord(true);
      const res = await trainingAPI.createRecord({
        training_id: recordForm.training_id,
        user_id: recordForm.user_id,
        training_date: recordForm.training_date,
        trainer_name: recordForm.trainer_name,
        is_completed: recordForm.is_completed,
        manual_reference: recordForm.manual_reference,
        manual_files: recordForm.manual_files
      });
      const created = res?.data?.data || {};

      if (created.lms_sync_status === 'synced') {
        toast.success('Logged — synced to AiventraLMS');
      } else if (created.lms_sync_status === 'failed') {
        toast.warn(`Logged — AiventraLMS sync failed (record still saved): ${created.lms_sync_error || 'unknown error'}`);
      } else if (created.lms_sync_status === 'manual') {
        toast.success('Training record logged (manual reference — LMS sync not purchased)');
      } else {
        toast.success('Training record logged');
      }

      await refreshRecords();
      setRecordForm(createEmptyRecordForm());
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to log training record');
    } finally {
      setSavingRecord(false);
    }
  };

  useEffect(() => {
    if (!location.state?.filterUsername) return;
    setRecordUserFilter(location.state.filterUsername);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const filteredRecords = records
    .filter((r) => matchesGroup(RECORD_GROUPS, recordCard, r))
    .filter((r) => !recordUserFilter || r.username === recordUserFilter);

  const [typePage, setTypePage] = useState(1);
  const [typePageSize, setTypePageSize] = useState(25);
  const typeTotalPages = Math.max(1, Math.ceil(trainingTypes.length / typePageSize));
  useEffect(() => { if (typePage > typeTotalPages) setTypePage(typeTotalPages); }, [typePage, typeTotalPages]);
  const paginatedTypes = trainingTypes.slice((typePage - 1) * typePageSize, typePage * typePageSize);

  const [recordPage, setRecordPage] = useState(1);
  const [recordPageSize, setRecordPageSize] = useState(25);
  const recordTotalPages = Math.max(1, Math.ceil(filteredRecords.length / recordPageSize));
  useEffect(() => { setRecordPage(1); }, [recordCard, recordPageSize]);
  useEffect(() => { if (recordPage > recordTotalPages) setRecordPage(recordTotalPages); }, [recordPage, recordTotalPages]);
  const paginatedRecords = filteredRecords.slice((recordPage - 1) * recordPageSize, recordPage * recordPageSize);

  const exportRecordsToExcel = () => {
    if (!filteredRecords.length) {
      toast.error('No training records to export');
      return;
    }
    const exportRows = filteredRecords.map((r) => ({
      'Training': r.training_name,
      'User': r.username,
      'Training Date': r.training_date ? new Date(r.training_date).toLocaleDateString() : '-',
      'Expiry Date': r.expiry_date ? new Date(r.expiry_date).toLocaleDateString() : '-',
      'Completed': r.is_completed ? 'Yes' : 'No',
      'LMS Sync': r.lms_sync_status || '-',
      'Approval Status': r.status || '-'
    }));
    const wb = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(wb, sheet, 'Training Records');
    XLSX.writeFile(wb, `Training_Records_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Training records exported to Excel');
  };

  const TYPE_IMPORT_HEADERS = {
    'training code': 'training_code', 'training name': 'training_name', 'training type': 'training_type',
    'document reference': 'document_reference', 'validity period (days)': 'validity_period', 'validity period': 'validity_period',
    'mandatory for stages': 'mandatory_for_stages', 'mandatory for roles': 'mandatory_for_roles',
  };
  const handleTypeImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      let rawRows;
      if (file.name.toLowerCase().endsWith('.csv')) {
        rawRows = await new Promise((resolve, reject) => Papa.parse(file, { header: true, skipEmptyLines: true, complete: (r) => resolve(r.data), error: reject }));
      } else {
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        rawRows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      }
      const rows = rawRows.map((raw) => {
        const row = {};
        Object.entries(raw).forEach(([header, value]) => {
          const field = TYPE_IMPORT_HEADERS[String(header).trim().toLowerCase()] || String(header).trim().toLowerCase().replace(/\s+/g, '_');
          row[field] = typeof value === 'string' ? value.trim() : value;
        });
        return row;
      }).filter((r) => Object.values(r).some((v) => String(v || '').trim() !== ''));
      if (!rows.length) { toast.error('No data rows found in file'); return; }
      setTypeImportPreview({ fileName: file.name, rows });
      setTypeImportResult(null);
    } catch (error) {
      toast.error('Failed to parse file. Use the template format.');
    } finally {
      event.target.value = '';
    }
  };
  const downloadTypeImportTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([{
      'Training Code': 'TRN-SOP-001', 'Training Name': 'Cleaning SOP Refresher', 'Training Type': 'SOP',
      'Document Reference': 'SOP/CLN/001', 'Validity Period (days)': 365, 'Mandatory For Stages': 'cleaning,visual', 'Mandatory For Roles': '',
    }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Training Types');
    XLSX.writeFile(wb, 'training_types_import_template.xlsx');
  };
  const confirmTypeImport = async () => {
    setTypeImporting(true);
    try {
      const response = await trainingAPI.importRows({ rows: typeImportPreview.rows });
      setTypeImportResult(response?.data?.data || null);
      setTypeImportPreview(null);
      await refreshTypes();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Import failed');
    } finally {
      setTypeImporting(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 className="page-title">Training</h2>
          <p className="page-subtitle">Manage training types, log completions, and track record validity.</p>
        </div>
        <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
          {lmsEmployeeSync&&<button className="btn btn-outline btn-sm" type="button" onClick={syncLmsEmployees} disabled={syncingLmsEmployees}>
            <FaSyncAlt className={syncingLmsEmployees?'fa-spin':''}/> {syncingLmsEmployees?'Syncing employees…':'Sync LMS employees'}
          </button>}
          <button className="btn btn-outline btn-sm" type="button" data-module="ai_assistant" onClick={() => ai.run('AI Training Gap Review', () => aiAPI.reviewData('training'), 'note')}>
            <FaRobot /> AI Training Gap Review
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Training Types</h2>
          {canManageTypes && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-outline btn-sm" type="button" onClick={downloadTypeImportTemplate}><FaDownload /> Template</button>
              <input ref={typeImportInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleTypeImportFile} />
              <button className="btn btn-outline btn-sm" type="button" onClick={() => typeImportInputRef.current?.click()}><FaFileUpload /> Import</button>
            </div>
          )}
        </div>
        {typeImportResult && (
          <div className="alert alert-info" style={{ margin: '0 16px 12px', fontSize: 'var(--fs-sm)' }}>
            Import complete: {typeImportResult.created} created, {typeImportResult.results.filter((r) => r.status === 'skipped').length} skipped, {typeImportResult.results.filter((r) => r.status === 'error').length} failed (of {typeImportResult.total} rows).
            {typeImportResult.results.some((r) => r.status !== 'created') && (
              <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                {typeImportResult.results.filter((r) => r.status !== 'created').map((r, i) => (
                  <li key={i}>Row {r.row}{r.training_code ? ` (${r.training_code})` : ''}: {r.message}</li>
                ))}
              </ul>
            )}
            <button className="btn btn-sm btn-outline" style={{ marginTop: '8px' }} type="button" onClick={() => setTypeImportResult(null)}>Dismiss</button>
          </div>
        )}
        {!canManageTypes && (
          <div style={{ padding: '8px 16px 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
            Only Admin / Super Admin can define training types.
          </div>
        )}
        <fieldset disabled={!canManageTypes} style={{ border: 'none', padding: 0, margin: 0 }}>
        <div style={{ padding: '16px' }}>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Training Code</label>
              <input className="form-control" value={typeForm.training_code} onChange={(e) => handleTypeField('training_code', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Training Name</label>
              <input className="form-control" value={typeForm.training_name} onChange={(e) => handleTypeField('training_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Training Type</label>
              <select className="form-control" value={typeForm.training_type} onChange={(e) => handleTypeField('training_type', e.target.value)}>
                {TRAINING_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Document Reference</label>
              <input className="form-control" value={typeForm.document_reference} onChange={(e) => handleTypeField('document_reference', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Validity Period (days)</label>
              <input className="form-control" type="number" min="0" value={typeForm.validity_period} onChange={(e) => handleTypeField('validity_period', e.target.value)} />
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Mandatory Before Signing Off</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {MANDATORY_STAGES.map((stage) => (
                  <label key={stage.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="checkbox"
                      checked={typeForm.mandatory_for_stages.includes(stage.key)}
                      onChange={() => toggleMandatoryStage(stage.key)}
                    />
                    {stage.label}
                  </label>
                ))}
              </div>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                When enforcement is on (Commercial Setup), personnel signing off the checked stage(s) must have this training current.
              </span>
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Mandatory For Roles</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {roles.map((role) => (
                  <label key={role.role_id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="checkbox"
                      checked={typeForm.mandatory_for_roles.includes(role.role_name)}
                      onChange={() => toggleMandatoryRole(role.role_name)}
                    />
                    {role.role_name}
                  </label>
                ))}
              </div>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                Leave all unchecked to apply to every role. Drives each user's Training compliance matrix (Users page) in addition to the sign-off gate above.
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button className="btn btn-primary" type="button" onClick={saveType} disabled={savingType || !canManageTypes}>
              {savingType ? 'Saving...' : 'Add Training Type'}
            </button>
          </div>
        </div>
        </fieldset>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Document Reference</th>
                <th>Validity (days)</th>
                <th>Mandatory For</th>
                <th>Active</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {trainingTypes.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-muted">No training types defined</td></tr>
              ) : paginatedTypes.map((type) => (
                <tr key={type.training_id}>
                  <td>{type.training_code}</td>
                  <td>{type.training_name}</td>
                  <td>{type.training_type}</td>
                  <td>{type.document_reference || '-'}</td>
                  <td>{type.validity_period ?? '-'}</td>
                  <td>{mandatoryBadge(type)}</td>
                  <td>{type.is_active === false ? 'No' : 'Yes'}</td>
                  <td>
                    {type.status === 'Draft' ? (
                      canManageTypes ? (
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => openApproveCourse(type)}>Approve</button>
                      ) : (
                        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--warning-text)' }}>Draft</span>
                      )
                    ) : (
                      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--success-text)' }}>Approved{lmsSyncEnabled ? ' · pushed to LMS' : ''}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {trainingTypes.length > 0 && (
          <TablePagination total={trainingTypes.length} page={typePage} pageSize={typePageSize} onPageChange={setTypePage} onPageSizeChange={(n) => { setTypePageSize(n); setTypePage(1); }} itemLabel="training types" />
        )}
      </div>

      <div className="card">
        <div className="card-header"><h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Log Training Completion</h2></div>
        {!canLogForOthers && (
          <div style={{ padding: '8px 16px 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
            You can log your own training completion here; Head QA will review and approve it. QA/Admin/Super Admin can log on behalf of other users.
          </div>
        )}
        <fieldset disabled={!canLogRecords} style={{ border: 'none', padding: 0, margin: 0 }}>
        <div style={{ padding: '16px' }}>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Training Type</label>
              <select className="form-control" value={recordForm.training_id} onChange={(e) => handleRecordField('training_id', e.target.value)}>
                <option value="">Select training</option>
                {trainingTypes.map((type) => (
                  <option key={type.training_id} value={type.training_id}>{type.training_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">User</label>
              {canLogForOthers ? (
                <select className="form-control" value={recordForm.user_id} onChange={(e) => handleRecordField('user_id', e.target.value)}>
                  <option value="">Select user</option>
                  {users.map((user) => (
                    <option key={user.user_id} value={user.user_id}>{user.employee_name || user.username}</option>
                  ))}
                </select>
              ) : (
                <input className="form-control" value={users.find((u) => String(u.user_id) === String(currentUserId))?.employee_name || 'You'} disabled readOnly />
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Training Date</label>
              <input className="form-control" type="date" value={recordForm.training_date} onChange={(e) => handleRecordField('training_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Trainer Name</label>
              <input className="form-control" value={recordForm.trainer_name} onChange={(e) => handleRecordField('trainer_name', e.target.value)} />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '24px' }}>
              <input
                id="training-is-completed"
                type="checkbox"
                checked={recordForm.is_completed}
                onChange={(e) => handleRecordField('is_completed', e.target.checked)}
              />
              <label htmlFor="training-is-completed" className="form-label" style={{ margin: 0 }}>Completed</label>
            </div>
            {!lmsSyncEnabled && (
              <>
                <div className="form-group">
                  <label className="form-label">Manual Reference No.</label>
                  <input
                    className="form-control"
                    placeholder="e.g. certificate / register number"
                    value={recordForm.manual_reference}
                    onChange={(e) => handleRecordField('manual_reference', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Certificate</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                    {recordForm.manual_files.map((f) => (
                      <span key={f.filename} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: 'var(--fs-md)' }}>
                        <a href={`${API_ORIGIN}/uploads/${f.filename}`} target="_blank" rel="noopener noreferrer" title={f.originalname}>
                          {f.originalname}
                        </a>
                        <button type="button" className="btn btn-sm btn-outline" onClick={() => removeManualFile(f.filename)} style={{ padding: '2px 6px' }}>
                          <FaTimes />
                        </button>
                      </span>
                    ))}
                    <label className="btn btn-sm btn-outline" style={{ cursor: 'pointer', margin: 0 }}>
                      <FaPaperclip style={{ fontSize: 'var(--fs-2xs)' }} /> {uploadingManual ? 'Uploading…' : 'Attach PDF'}
                      <input
                        type="file"
                        accept="application/pdf"
                        style={{ display: 'none' }}
                        disabled={uploadingManual}
                        onChange={(e) => { uploadManualFile(e.target.files?.[0]); e.target.value = ''; }}
                      />
                    </label>
                  </div>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                    AiventraLMS sync is not purchased for this client — record a manual reference and/or attach the training certificate instead.
                  </span>
                </div>
              </>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button className="btn btn-primary" type="button" onClick={saveRecord} disabled={savingRecord || !canLogRecords}>
              {savingRecord ? 'Saving...' : 'Log Completion'}
            </button>
          </div>
        </div>
        </fieldset>
      </div>

      {typeImportPreview && (
        <Modal isOpen size="lg" title={`Import ${typeImportPreview.rows.length} Training Type(s) — ${typeImportPreview.fileName}`} onClose={() => setTypeImportPreview(null)}>
          <FloatingScrollWrapper>
            <table style={{ width: '100%', fontSize: 'var(--fs-sm)', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Code</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Validity (days)</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Mandatory For</th>
                </tr>
              </thead>
              <tbody>
                {typeImportPreview.rows.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '6px 10px' }}>{row.training_code || <span style={{ color: 'var(--danger-text)' }}>Missing</span>}</td>
                    <td style={{ padding: '6px 10px' }}>{row.training_name || <span style={{ color: 'var(--danger-text)' }}>Missing</span>}</td>
                    <td style={{ padding: '6px 10px' }}>{row.training_type || '—'}</td>
                    <td style={{ padding: '6px 10px' }}>{row.validity_period || '—'}</td>
                    <td style={{ padding: '6px 10px' }}>{row.mandatory_for_stages || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </FloatingScrollWrapper>
          <ModalFooter>
            <button className="btn btn-outline" type="button" onClick={() => setTypeImportPreview(null)}>Cancel</button>
            <button className="btn btn-primary" type="button" disabled={typeImporting} onClick={confirmTypeImport}>
              {typeImporting ? 'Importing…' : `Import ${typeImportPreview.rows.length} Row(s)`}
            </button>
          </ModalFooter>
        </Modal>
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Training Records</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {recordUserFilter && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '999px', background: 'var(--info-bg)', color: 'var(--info-text)', fontSize: 'var(--fs-sm)' }}>
                User: {recordUserFilter}
                <button type="button" onClick={() => setRecordUserFilter('')} title="Clear filter" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700, lineHeight: 1 }}>&times;</button>
              </span>
            )}
            <button className="btn btn-success btn-sm" type="button" onClick={exportRecordsToExcel}><FaDownload /> Excel Export</button>
          </div>
        </div>
        <div style={{ padding: '12px 16px 0' }}>
          <FilterStatCards groups={RECORD_GROUPS} records={records} value={recordCard} onChange={setRecordCard} totalTitle="Total Records" totalIcon={FaGraduationCap} />
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Training</th>
                <th>User</th>
                <th>Training Date</th>
                <th>Expiry Date</th>
                <th>Completed</th>
                <th>LMS Sync</th>
                <th>Approval</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted">No data available</td></tr>
              ) : paginatedRecords.map((record) => (
                <tr key={record.record_id}>
                  <td>{record.training_name}</td>
                  <td>{record.username}</td>
                  <td>{record.training_date ? new Date(record.training_date).toLocaleDateString() : '-'}</td>
                  <td>{expiryBadge(record.expiry_date)}</td>
                  <td>{record.is_completed ? 'Yes' : 'No'}</td>
                  <td>{lmsSyncBadge(record)}</td>
                  <td>
                    {record.status === 'Approved' ? (
                      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--success-text)', fontWeight: 600 }}>Approved{record.qa_approved_by_name ? ` by ${record.qa_approved_by_name}` : ''}</span>
                    ) : canApproveRecords ? (
                      <button className="btn btn-outline btn-sm" type="button" onClick={() => openApproveRecord(record)}>Approve</button>
                    ) : (
                      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--warning-text)' }}>Pending Head QA</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredRecords.length > 0 && (
          <TablePagination total={filteredRecords.length} page={recordPage} pageSize={recordPageSize} onPageChange={setRecordPage} onPageSizeChange={(n) => { setRecordPageSize(n); setRecordPage(1); }} itemLabel="records" />
        )}
      </div>

      {esign && (
        <Modal
          isOpen
          onClose={closeEsign}
          title={`${esign.kind === 'record' ? `Approve Training Record — ${esign.row.training_name} (${esign.row.username})` : `Approve Training Protocol — ${esign.row.training_name}`} — Electronic Signature`}
          footer={
            <>
              <button className="btn btn-outline" onClick={closeEsign} disabled={esigning}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmEsign} disabled={esigning}>{esigning ? 'Processing...' : 'Confirm Approval'}</button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Comment</label>
            <textarea className="form-control" rows={2} value={esign.comment} onChange={(e) => setEsign((p) => ({ ...p, comment: e.target.value }))} placeholder="Optional approval comment" />
          </div>
          <div className="form-group">
            <label className="form-label">Login Password *</label>
            <input type="password" className="form-control" autoComplete="new-password" value={esign.password}
              onChange={(e) => setEsign((p) => ({ ...p, password: e.target.value }))}
              placeholder="Enter your login password"
              onKeyDown={(e) => { if (e.key === 'Enter') confirmEsign(); }} />
          </div>
        </Modal>
      )}

      <AIResultPanel
        isOpen={ai.isOpen}
        onClose={ai.close}
        title={ai.title}
        loading={ai.loading}
        error={ai.error}
        content={ai.content}
        docType="note"
        exportFileName="training-ai-review.pdf"
      />
    </div>
  );
};

export default Training;
